(() => {
  const defineQuickAddModal = () => {
    // The tag MUST stay `quick-add-modal`: the frozen v11 embed's product-form closes the
    // quick view after add-to-cart via closest('quick-add-modal'), and that asset cannot be
    // edited in place (a rename needs a coordinated a-v12 embed deploy). Host-theme collision
    // on the shared tag is handled per direction: if the host registers first we skip (its
    // implementation upgrades our shells, whose closed state then comes from the host's own
    // .quick-add-modal CSS); if we register first, the constructor below only claims elements
    // carrying the Atlas `atlas-quick-add` marker class, so host modals keep their styling.
    if (customElements.get('quick-add-modal')) return;
    const ModalDialogBase = window.ModalDialog || customElements.get('modal-dialog');
    customElements.define('quick-add-modal', class QuickAddModal extends ModalDialogBase {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      // The dialog layer hoists this element to <body>, outside the section's
      // .atlas-isolation-wrapper — carry the wrapper class so all Atlas-scoped CSS
      // (product layout, popup styling, CSS variables) applies to the injected content.
      // Atlas shells only: on a shared-tag host this class also upgrades the host's own
      // quick-add modals, which must keep the host styling.
      if (this.classList.contains('atlas-quick-add')) {
        this.classList.add('atlas-isolation-wrapper');
      }
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      // Host-owned shells upgraded by this class may lay out their content differently.
      if (this.modalContent) this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

          // Only Atlas product pages expose a `MainProduct-` section that this modal can host.
          // When the product uses the host theme's own PDP template (common on generated stores
          // for products that never had an Atlas product page built), there is nothing we can
          // safely inject — the host theme's gallery and buy buttons don't rebind inside the
          // modal (no image, dead add-to-cart). Fall back to opening the full product page.
          if (!this.productElement) {
            window.location.href = opener.getAttribute('data-product-url');
            return;
          }

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .catch(() => {
          // Network failure or unexpected markup: never leave a dead button or a
          // scroll-locked empty modal — the product page can always serve the purchase.
          window.location.href = opener.getAttribute('data-product-url');
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPickers = this.modalContent.querySelectorAll('hybrid-variant-picker,variant-radios');

      if (!variantPickers.length) return;

      variantPickers.forEach(variantPicker => {
        variantPicker.setAttribute('data-update-url', 'false');
      });
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const mediaWrapper = product && product.querySelector('.product__media-wrapper');
      // A media-less product renders no media wrapper — nothing to resize.
      if (!mediaWrapper) return;
      const desktopColumns = mediaWrapper.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.querySelector('.product__media-wrapper').classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.querySelector('.product__media-wrapper').classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
    });
  };

  // On host themes the Atlas globals (including ModalDialog) are injected dynamically by the app
  // embed, so this file can execute before ModalDialog exists. Define immediately when possible,
  // otherwise wait for the base element's registration instead of crashing with a ReferenceError.
  if (window.ModalDialog || customElements.get('modal-dialog')) {
    defineQuickAddModal();
  } else {
    customElements.whenDefined('modal-dialog').then(defineQuickAddModal);
  }
})();
